// `fuzzPostcode`, `defaultAgeAnonymiser` and `preserveAnonymiser` now live in
// `@thalamos/common` (shared with the Aspire anonymisation layer). The location
// fuzzer below still uses `fuzzPostcode` internally.
import { fuzzPostcode } from "@thalamos/common";

import type { LocationUnbranded } from "../types/location.js";
import {
  DEFAULT_LOCATION_PRECISION,
  findMostPreciseOverride,
  type AnonymisedGeoLocation,
  type LocationPrecisionOverrideUnbranded,
} from "../types/locationPrecisionOverride.js";
import type { UnknownableValue, Value } from "../types/workflow/values.js";

/**
 * Unique symbol used to mark location fuzzer anonymiser functions.
 * This enables reliable identification even if functions are wrapped or recreated.
 */
export const LOCATION_FUZZER_MARKER = Symbol.for(
  "thalamos:locationFuzzerAnonymiser",
);

/**
 * Type for anonymiser functions that can be identified as location fuzzers.
 * Uses `any` for the function signature to allow assignability with specific parameter types.
 */
export type MarkedAnonymiserFunction = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (...args: any[]): any;
  [LOCATION_FUZZER_MARKER]?: true;
};

/**
 * Checks if a function is a location fuzzer anonymiser.
 */
export function isLocationFuzzerAnonymiser(
  fn: unknown,
): fn is MarkedAnonymiserFunction {
  return (
    typeof fn === "function" &&
    (fn as MarkedAnonymiserFunction)[LOCATION_FUZZER_MARKER] === true
  );
}

// do not preserve user entered addresses at all, and attempt to 'fuzz' lat/long
// and postal addresses
// TODO: we should probably only preserve the primary address type to prevent
// re-identification based on intersecting partial postcode and partial lat/long

/**
 * Anonymises a geo location by rounding coordinates to the specified precision.
 * If precision overrides are provided, uses the most precise matching override
 * for the given coordinates.
 */
function anonymiseGeoLocation(
  geoLocation: { latitude: number; longitude: number },
  precisionOverrides: LocationPrecisionOverrideUnbranded[] = [],
): AnonymisedGeoLocation {
  const override = findMostPreciseOverride(
    geoLocation.latitude,
    geoLocation.longitude,
    precisionOverrides,
  );

  const precision = override?.precision ?? DEFAULT_LOCATION_PRECISION;
  const multiplier = 1 / precision;

  return {
    latitude: Math.round(geoLocation.latitude * multiplier) / multiplier,
    longitude: Math.round(geoLocation.longitude * multiplier) / multiplier,
    precision,
    ...(override ? { precisionOverrideId: override.id } : {}),
  };
}

/**
 * Creates a location fuzzer anonymiser with the given precision overrides.
 * This is a factory function that returns an anonymiser function bound to specific overrides.
 * The returned function is marked with LOCATION_FUZZER_MARKER for reliable identification.
 */
export const createLocationFuzzerAnonymiser = (
  precisionOverrides: LocationPrecisionOverrideUnbranded[] = [],
): MarkedAnonymiserFunction => {
  const anonymiser: MarkedAnonymiserFunction = (
    location:
      | Value<LocationUnbranded>
      | UnknownableValue<LocationUnbranded>
      | null,
  ) => locationFuzzerAnonymiser(location, precisionOverrides) as unknown;

  // Mark the function for reliable identification
  anonymiser[LOCATION_FUZZER_MARKER] = true;

  return anonymiser;
};

/**
 * Anonymises location data by fuzzing coordinates and postcodes.
 * This function is marked with LOCATION_FUZZER_MARKER for reliable identification
 * in bindPrecisionOverridesToAnonymiser.
 */
export const locationFuzzerAnonymiser: MarkedAnonymiserFunction = Object.assign(
  (
    location:
      | Value<LocationUnbranded>
      | UnknownableValue<LocationUnbranded>
      | null,
    precisionOverrides: LocationPrecisionOverrideUnbranded[] = [],
  ) => {
    if (!location) return { value: null, isUnknown: true };
    const { value } = location;
    if (!value) return { value: null, isUnknown: true };
    return {
      isUnknown: "isUnknown" in location ? false : undefined,
      value: {
        primaryLocationType: value.primaryLocationType,

        geoLocation: value.geoLocation
          ? anonymiseGeoLocation(value.geoLocation, precisionOverrides)
          : undefined,
        postalAddress: value.postalAddress
          ? value.postalAddress
              .filter(({ types }) => types.includes("postal_code"))
              .map(({ longName, shortName, types }) => ({
                longName: fuzzPostcode(longName),
                shortName: fuzzPostcode(shortName),
                types: types,
              }))
          : undefined,
      },
    };
  },
  { [LOCATION_FUZZER_MARKER]: true as const },
);
